// ═══════════════════════════════════════════════════════════════════════════
// OUTFORM · HISTORICAL COMPARABLES — THE WHOLE FLOW, SCREEN BY SCREEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Erez, 2 Sept 2026, by voice: "The Data Pilot demo shows in high detail how
// they're doing the flow of the ballpark. We're doing it differently from the
// perspective that we're not doing it in a wizard with changing screens — we do
// it all in one screen. Show the whole flow of the comparable jobs as we will
// do it on one page, but bring as much as you can the logic, and show all the
// steps by showing screens of the whole flow."
//
// ⭐ SO THE ARGUMENT THIS SECTION MAKES IS THE DIFFERENCE ITSELF. The Data Pilot
// runs a SIX-STEP RAIL — 1 Historical Comparables · 2 Production Option · 3
// Generate Ballpark · 4 Confirm · 5 Under Review · 6 Final Decision — and each
// step REPLACES the screen. Ours is ONE page whose comparables SECTION changes
// state in place, between the job block and the component list. Nine frames
// below are nine states of the same page, never nine pages.
//
// ⛔ WHY THAT IS NOT A STYLE PREFERENCE. In a wizard the estimator cannot see
// what a choice did, because the thing it changed is on a screen he has left.
// The whole value of a comparable is that it SEEDS the component list — so the
// component list has to be visible while he chooses. A rail hides exactly the
// consequence it exists to produce.
//
// ⛔ NOTHING HERE IS INVENTED, AND THE MECHANICS ARE READ OUT OF THE CODE, not
// remembered: reads.find_comps (the seven weights, NOT_EVIDENCE, dense rank,
// ranking_note), ballpark.shortlist, ballpark.choose, ballpark._seed_components
// (the assembly rollup and the zero-cost skip), api.py:172/184, and
// sql/020_outform_domain.sql:247 for what estimate_comps can and cannot store.
// The FOUR RED MARKS below are things the code does not do yet — they are the
// server gaps in MJOS-246 and they are drawn as absent rather than left out.
//
// ⚠ ILLUSTRATIVE, and marked on screen: the comparable job numbers and their
// cost figures, the CM percentages (outcome is mastered in Sugar, which is not
// connected — every job's outcome is NULL), and the assembly names. The SHAPES
// are real; the numbers stand in.
//
// This is a DESIGN on the board. It holds no fetch and touches no application
// code, on purpose.

import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, Plus, Search, Sparkles, TriangleAlert, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ─────────────────────────────────────────────────────────────────────────────
// THE SKIN — lifted unchanged from Jarvis's `command/outform-input-ux.tsx`, so
// this section and his input forms are visibly the same product. Copied rather
// than imported: his file is a design he owns and I must not edit it, and a
// cross-board import would make one board's build depend on another's.
// ─────────────────────────────────────────────────────────────────────────────

const CARD = "rounded-[22px] shadow-[0_1px_3px_rgba(16,24,40,0.05)]";
const INPUT = "border-transparent bg-[#F4F4F7] rounded-xl shadow-none";
const PAGE = "bg-[#EEEEF1]"; // the coloured wash the white cards float on

function SCard({
  title, desc, action, className = "", children,
}: {
  title: string; desc?: React.ReactNode; action?: React.ReactNode;
  className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`${CARD} bg-white text-neutral-900 ${className}`}>
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em]">{title}</h3>
          {desc ? <p className="mt-0.5 text-[11px] leading-snug text-neutral-500">{desc}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function Band({ n, children, tone = "dark" }: { n: string; children: React.ReactNode; tone?: "dark" | "live" }) {
  return (
    <div className="flex items-center gap-2.5 pt-1">
      <span
        className={`flex size-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${
          tone === "live" ? "bg-sky-600" : "bg-neutral-900"
        }`}
      >
        {n}
      </span>
      <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-neutral-700">{children}</span>
    </div>
  );
}

/** Because a demo number that cannot say it is a demo number is a lie. */
function Illus({ children = "illustrative" }: { children?: React.ReactNode }) {
  return (
    <Badge variant="outline" className="border-amber-400 bg-amber-100 px-1.5 py-0 text-[9px] font-semibold text-amber-900">
      <TriangleAlert className="size-2.5" />
      {children}
    </Badge>
  );
}

/** A thing the code does not do yet. Drawn as ABSENT, never left out. */
function Gap({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-rose-400 bg-rose-50 px-1.5 py-[1px] text-[9.5px] font-semibold text-rose-800">
      not built · {children}
    </span>
  );
}

function Chip({ children, tone = "plain" }: { children: React.ReactNode; tone?: "plain" | "match" | "src" | "typed" }) {
  const skin =
    tone === "match" ? "border-sky-300 bg-sky-50 text-sky-900"
    : tone === "src" ? "border-violet-300 bg-violet-50 text-violet-900"
    : tone === "typed" ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : "border-neutral-300 bg-neutral-50 text-neutral-700";
  return (
    <span className={`inline-flex items-center rounded-md border px-1.5 py-[1px] text-[9.5px] font-medium ${skin}`}>
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE FRAME — one state of the ONE page, plus the logic underneath it
// ─────────────────────────────────────────────────────────────────────────────
//
// The three-line footer is the whole reason these are frames and not a picture:
// SERVER says what actually runs, ESTIMATOR says what he does, RECORDED says
// what survives afterwards. A screen without those three is decoration.

function Frame({
  step, state, title, blurb, server, human, recorded, w = 620, children,
}: {
  step: string; state: string; title: string; blurb: string;
  server: React.ReactNode; human: React.ReactNode; recorded: React.ReactNode;
  w?: number; children: React.ReactNode;
}) {
  return (
    <div style={{ width: w }} className="flex shrink-0 flex-col gap-2.5">
      <div className="flex items-baseline gap-2 px-1">
        <span className="flex size-6 items-center justify-center rounded-full bg-neutral-900 text-[11px] font-bold text-white">
          {step}
        </span>
        <span className="text-[15px] font-bold tracking-tight text-neutral-900">{title}</span>
        <Badge variant="outline" className="border-neutral-400 bg-white px-1.5 py-0 font-mono text-[9.5px] text-neutral-700">
          {state}
        </Badge>
      </div>
      <p className="px-1 text-[11.5px] leading-snug text-neutral-600">{blurb}</p>

      {/* the page mock */}
      <div className={`${PAGE} rounded-[26px] p-4`}>
        <div className="flex flex-col gap-3">{children}</div>
      </div>

      {/* the logic */}
      <div className={`${CARD} bg-white`}>
        {([
          ["SERVER", server, "text-sky-800 bg-sky-50 border-sky-300"],
          ["ESTIMATOR", human, "text-neutral-800 bg-neutral-100 border-neutral-300"],
          ["RECORDED", recorded, "text-violet-900 bg-violet-50 border-violet-300"],
        ] as const).map(([k, v, skin]) => (
          <div key={k} className="flex gap-2.5 border-b border-neutral-100 px-4 py-2.5 last:border-b-0">
            <span className={`h-fit shrink-0 rounded-md border px-1.5 py-[1px] text-[9px] font-bold tracking-wider ${skin}`}>
              {k}
            </span>
            <p className="text-[11px] leading-relaxed text-neutral-700">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── the three fixed neighbours, compressed ───────────────────────────────────

function JobBlock({ filled = true }: { filled?: boolean }) {
  return (
    <>
      <Band n="1">The job</Band>
      <SCard title="The job" desc="Their eight fields, their labels, their order — Setup!B2.">
        <div className="grid grid-cols-12 gap-2.5">
          <div className="col-span-5 flex flex-col gap-1">
            <Label className="text-[10.5px] font-semibold text-neutral-800">Customer</Label>
            <Input readOnly className={`h-8 text-[11.5px] ${INPUT}`} value={filled ? "MORPHE" : ""} />
          </div>
          <div className="col-span-7 flex flex-col gap-1">
            <Label className="text-[10.5px] font-semibold text-neutral-800">Project Description</Label>
            <Input readOnly className={`h-8 text-[11.5px] ${INPUT}`} value={filled ? "Sephora 2-bay permanent merchandiser" : ""} />
          </div>
          <div className="col-span-4 flex flex-col gap-1">
            <Label className="text-[10.5px] font-semibold text-neutral-800">Vertical</Label>
            <Input readOnly className={`h-8 text-[11.5px] ${INPUT}`} value={filled ? "Beauty" : ""} />
          </div>
          <div className="col-span-4 flex flex-col gap-1">
            <Label className="text-[10.5px] font-semibold text-neutral-800">Client Budget / Unit</Label>
            <Input readOnly className={`h-8 text-[11.5px] ${INPUT}`} value={filled ? "500" : ""} />
          </div>
          <div className="col-span-4 flex flex-col gap-1">
            <Label className="text-[10.5px] font-semibold text-neutral-800">Commercial Lead</Label>
            <Input readOnly className={`h-8 text-[11.5px] ${INPUT}`} value={filled ? "Tina DelFiacco" : ""} />
          </div>
        </div>
      </SCard>
    </>
  );
}

const SEEDED = [
  { d: "S1 Core — 2B CAN", q: 1, c: "38.40", from: "264714" },
  { d: "S1 left area, shelf + bracket", q: 1, c: "22.15", from: "264714" },
  { d: "S2 backstock dividers", q: 1, c: "9.80", from: "264714" },
  { d: "Back panels, printed", q: 1, c: "17.25", from: "262919" },
  { d: "Header, litho-mounted", q: 1, c: "6.90", from: "262919" },
  { d: "Drawer, lock and cover", q: 1, c: "14.05", from: "264714" },
  { d: "Mirror panel, polished edge", q: 1, c: "6.20", from: null },
];

function ComponentList({ seeded = false }: { seeded?: boolean }) {
  const rows = seeded ? SEEDED : [];
  return (
    <>
      <Band n="3">The component list</Band>
      <SCard
        title="The component list"
        desc={seeded ? "Assembly level — never 389 parts. Every seeded row names the job it came from." : undefined}
        action={seeded ? <Illus /> : undefined}
      >
        {rows.length === 0 ? (
          <p className="py-6 text-center text-[11.5px] text-neutral-400">
            Nothing yet. Choose a comparable above, or add a row.
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="[&_th]:pb-1.5 [&_th]:text-left [&_th]:text-[9.5px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-neutral-600">
                <th>Description</th><th className="w-14">Qty</th>
                <th className="w-20 text-right">Unit</th><th className="w-28">Origin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.d} className="border-t border-neutral-100 [&_td]:py-1.5 [&_td]:text-[11px]">
                  <td className="text-neutral-800">{r.d}</td>
                  <td className="tabular-nums text-neutral-600">{r.q}</td>
                  <td className="text-right tabular-nums text-neutral-900">${r.c}</td>
                  <td>
                    {r.from
                      ? <Chip tone="src">from {r.from}</Chip>
                      : <Chip tone="typed">typed by you</Chip>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SCard>
    </>
  );
}

// ── the comparable card ──────────────────────────────────────────────────────

type Comp = {
  job: string; client: string; what: string; when: string;
  score: string; rank: number; cost: string; units: number; cm: string;
  matched: string[]; differs: string;
};

const COMPS: Comp[] = [
  {
    job: "264714", client: "MORPHE", what: "Sephora 2-bay permanent merchandiser", when: "Mar 2025",
    score: "0.94", rank: 1, cost: "$441,666", units: 127, cm: "26.8%",
    matched: ["display type — exact (0.25)", "client — MORPHE (0.20)", "units 127 vs 120 (0.09)", "retailer — Sephora (0.10)"],
    differs: "Illuminated header on that build, not on this concept · Mar 2025 pricing, steel index −2.1% since",
  },
  {
    job: "262919", client: "L'ORÉAL", what: "Inline merchandiser, 4ft", when: "Sep 2025",
    score: "0.71", rank: 2, cost: "$223,200", units: 400, cm: "27.1%",
    matched: ["display type — exact (0.25)", "retailer — Sephora (0.10)", "vertical — Beauty (0.05)"],
    differs: "400 units against your 120 — a different volume band · no drawer assembly",
  },
  {
    job: "243854", client: "SEPHORA COLLECTION", what: "Beauty endcap, 3-bay", when: "Jun 2024",
    score: "0.71", rank: 2, cost: "$318,400", units: 210, cm: "24.2%",
    matched: ["display type — exact (0.25)", "retailer — Sephora (0.10)", "vertical — Beauty (0.05)"],
    differs: "Endcap rather than inline · two generations old, before the current costing sheet",
  },
];

function CompCard({
  c, open = false, chosen = false, compact = false, onToggle,
}: {
  c: Comp; open?: boolean; chosen?: boolean; compact?: boolean; onToggle?: () => void;
}) {
  return (
    <div className={`rounded-2xl bg-white p-3.5 ${chosen ? "ring-2 ring-neutral-900" : "shadow-[0_1px_2px_rgba(16,24,40,0.04)]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[11px] font-bold text-neutral-900">{c.job}</span>
            <span className="text-[12px] font-semibold text-neutral-900">{c.client}</span>
            <span className="text-[10px] text-neutral-500">{c.when}</span>
          </div>
          <p className="mt-0.5 text-[11px] text-neutral-600">{c.what}</p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[19px] font-bold leading-none tabular-nums text-neutral-900">{c.score}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-neutral-500">rank {c.rank}</div>
        </div>
      </div>

      {/* ⭐ THE SCORE IS A FIGURE, NOT A NUMBER — chapter 14. Its `basis` names
          every term that produced it, which IS the Data Pilot's "why this
          project was selected" panel, out of the contract we already have. */}
      <div className="mt-2.5 flex flex-wrap gap-1">
        {c.matched.map((m) => <Chip key={m} tone="match">{m}</Chip>)}
      </div>

      <p className="mt-2 text-[10.5px] leading-snug text-neutral-600">
        <span className="font-semibold text-neutral-800">Differs — </span>{c.differs}
      </p>

      <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-2.5">
        <span className="text-[10.5px] tabular-nums text-neutral-700">{c.cost} · {c.units} units</span>
        <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-rose-400 bg-rose-50 px-1.5 py-[1px] text-[9.5px] font-semibold text-rose-800">
          won @ CM {c.cm} — PLACEHOLDER, outcome is in Sugar and Sugar is not connected
        </span>
      </div>

      {!compact ? (
        <div className="mt-2.5 flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onToggle}
            className="h-6 gap-1 border-neutral-300 px-2 text-[10.5px]">
            {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            {open ? "Hide its bill of material" : "Show its bill of material"}
          </Button>
          <Button size="sm" className={`h-6 px-2 text-[10.5px] ${chosen ? "" : "bg-neutral-900"}`}>
            {chosen ? "Keeping this one" : "Keep"}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[10.5px] text-neutral-500">
            <X className="size-3" /> Exclude
          </Button>
        </div>
      ) : null}

      {open ? <CompBom /> : null}
    </div>
  );
}

/** One comparable's BOM, rolled to ASSEMBLY — exactly what `_seed_components`
 *  does: split_part(level,'.',1), sum of children, and an assembly whose parts
 *  all price at zero is SKIPPED rather than shown as a row of noise. */
function CompBom() {
  const [drilled, setDrilled] = useState(false);
  const rows = [
    { a: "1", n: "S1 Core — 2B CAN", ext: "38.40", parts: 16 },
    { a: "2", n: "S1 left area, shelf + bracket", ext: "22.15", parts: 9 },
    { a: "3", n: "S2 backstock dividers", ext: "9.80", parts: 4 },
    { a: "4", n: "Drawer, lock and cover", ext: "14.05", parts: 7 },
    { a: "5", n: "Back panels, printed", ext: "17.25", parts: 3 },
  ];
  return (
    <div className="mt-3 rounded-xl bg-[#F7F7F9] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[9.5px] font-bold uppercase tracking-wider text-neutral-600">
          its bill of material — assembly level
        </span>
        <span className="text-[9.5px] font-semibold text-neutral-500">
          5 of 9 assemblies carried a cost · 4 skipped, all parts at zero
        </span>
      </div>
      <table className="w-full">
        <tbody>
          {rows.map((r) => (
            <tr key={r.a} className="border-t border-neutral-200 first:border-t-0 [&_td]:py-1 [&_td]:text-[10.5px]">
              <td className="w-6 font-mono text-neutral-500">{r.a}</td>
              <td className="text-neutral-800">
                <button className="text-left hover:underline" onClick={() => r.a === "1" && setDrilled(!drilled)}>
                  {r.n}
                </button>
                <span className="ml-1.5 text-neutral-400">({r.parts} parts)</span>
              </td>
              <td className="w-16 text-right tabular-nums text-neutral-900">${r.ext}</td>
            </tr>
          ))}
          {drilled ? (
            <>
              <tr className="bg-white/70"><td /><td className="pl-3 pt-1 text-[9.5px] font-bold uppercase tracking-wider text-neutral-500">1 · its parts — fetched on this click, never before</td><td /></tr>
              {[["1.1", "ADH-00042-003 · Tape bron clr .03", "0.26"],
                ["1.2", "DS-LS002CL · Laser-cut side, clear", "11.40"],
                ["1.3", "TR-4412 · Powder-coat steel frame", "18.90"],
                ["1.4", "HW-0071 · Magnet set, 8mm", "1.84"]].map(([l, n, c]) => (
                <tr key={l} className="bg-white/70 [&_td]:py-[3px] [&_td]:text-[10px]">
                  <td className="pl-2 font-mono text-neutral-400">{l}</td>
                  <td className="pl-3 text-neutral-600">{n}</td>
                  <td className="text-right tabular-nums text-neutral-700">${c}</td>
                </tr>
              ))}
            </>
          ) : null}
        </tbody>
      </table>
      <p className="mt-2 text-[9.5px] text-neutral-500">
        Click an assembly to open its parts. <Illus /> shapes are real, figures stand in.
      </p>
    </div>
  );
}

/** The section itself — the one thing on this page that changes. */
function CompsSection({
  state, children, sentence,
}: {
  state: "idle" | "searching" | "open" | "settled";
  children?: React.ReactNode; sentence?: React.ReactNode;
}) {
  const live = state === "open" || state === "searching";
  return (
    <>
      <Band n="2" tone={live ? "live" : "dark"}>Comparable jobs</Band>
      <SCard
        title="Have we built this before?"
        desc={state === "open" ? "3 shown of 14 jobs on file — 5 in this vertical. Pick the ones you would have used." : undefined}
        action={state === "open" ? <Button size="sm" className="h-7 bg-neutral-900 px-3 text-[11px]">Use these — seed the bill of material</Button> : undefined}
      >
        {sentence ? (
          <div className="flex items-center gap-2 py-1">
            {state === "searching" ? <Search className="size-3.5 animate-pulse text-neutral-400" /> : null}
            <p className="text-[12px] leading-snug text-neutral-700">{sentence}</p>
          </div>
        ) : null}
        {children}
      </SCard>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE NINE FRAMES
// ─────────────────────────────────────────────────────────────────────────────

export function ComparablesFlow() {
  const [openCard, setOpenCard] = useState(true);

  return (
    <div className="nopan nodrag w-[2020px] text-neutral-900" onWheelCapture={(e) => e.stopPropagation()}>
      {/* THE ARGUMENT, STATED ONCE AT THE TOP */}
      <div className="mb-5 rounded-[22px] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.05)]">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
          OUTFORM · JOURNEY A · THE COMPARABLES SECTION · MJOS-246
        </p>
        <h2 className="mt-1.5 text-[25px] font-bold leading-tight tracking-tight">
          One page whose middle section changes — not a six-step wizard
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-8">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">The Data Pilot</p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-neutral-700">
              A six-step rail — <span className="font-mono text-[11.5px]">1 Historical Comparables · 2 Production Option · 3 Generate
              Ballpark · 4 Confirm · 5 Under Review · 6 Final Decision</span> — where each step REPLACES the screen.
              The room reacted to the comparables coming back as objects they could pick from, and they were right to.
            </p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Ours</p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-neutral-700">
              <span className="font-semibold">One screen. Three bands: the job · the comparables · the component list.</span> Only the
              middle one changes state. <span className="font-semibold">In a wizard the estimator cannot see what his choice did</span>,
              because the thing it changed is on a screen he has left — and the entire value of a comparable is that it SEEDS the
              component list. A rail hides the consequence it exists to produce.
            </p>
          </div>
        </div>
        <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
          <span className="text-[10.5px] font-bold uppercase tracking-wider text-neutral-500">Read the frames as</span>
          <Chip tone="match">SERVER — what actually runs</Chip>
          <Chip>ESTIMATOR — what he does</Chip>
          <Chip tone="src">RECORDED — what survives</Chip>
          <Gap>a server gap, drawn as absent</Gap>
          <Illus />
        </div>
      </div>

      <div className="flex flex-wrap gap-6">

        {/* 1 ── IDLE ─────────────────────────────────────────────────────── */}
        <Frame
          step="1" state="idle" title="It is already there, saying nothing yet"
          blurb="The section exists from the moment the page opens. It never appears and never disappears — that is the whole design, and it is what lets the ending mean something."
          server="Nothing has run. There is not enough of a job to search on."
          human="Starts filling the job block. He has not asked for anything."
          recorded="Nothing."
        >
          <JobBlock filled={false} />
          <CompsSection state="idle" sentence={<span className="text-neutral-500">Add a customer and a description and I will look for comparable jobs.</span>} />
          <ComponentList />
        </Frame>

        {/* 2 ── SEARCHING ───────────────────────────────────────────────── */}
        <Frame
          step="2" state="searching" title="It wakes on its own"
          blurb="No Search button. The moment customer and description exist, it goes and looks — and says how many jobs it is looking through, because with a corpus this small the denominator is the honest part."
          server={<>
            <span className="font-mono">GET /ballparks/&#123;id&#125;/shortlist?limit=5</span> → <span className="font-mono">reads.find_comps</span>.
            <span className="font-semibold"> NOT_EVIDENCE</span> is applied first: no fixtures · must carry a source file ·
            <span className="font-mono"> origin='workbook'</span> — so a ballpark typed this morning cannot price the next job this
            afternoon. And <span className="font-mono">exclude_job_id</span> stops the job being its own top comparable, which
            actually happened on this box on 10 August.
          </>}
          human="Nothing. He is still typing."
          recorded="Nothing yet. Searching is not a decision."
        >
          <JobBlock />
          <CompsSection state="searching" sentence={<>Looking through <span className="font-semibold">14 jobs</span> on file…</>} />
          <ComponentList />
        </Frame>

        {/* 3 ── THE CARDS ───────────────────────────────────────────────── */}
        <Frame
          step="3" state="open" title="Comparables that argue their own case" w={720}
          blurb="A card, never a row. The score is not a badge — it is a figure whose basis names every term that produced it. That IS the Data Pilot's best idea, and our payload contract gives it to us for free."
          server={<>
            Seven weighted terms — display_type .25 · client .20 · pv_band .20 · retailer .10 · units .10 · budget .10 · vertical .05.
            <span className="font-semibold"> Dense rank: two jobs it cannot separate SHARE a rank</span>, and the payload says so in
            words. Outcome comes back <span className="font-mono">NULL</span> with a sentence, never a defaulted 0.5.
          </>}
          human="Reads them. Excludes the ones he disagrees with. Keeps one or more — or none."
          recorded={<>Nothing until he closes the section. Looking is not choosing. <Gap>the reviewed set has nowhere to be stored</Gap></>}
        >
          <JobBlock />
          <CompsSection state="open">
            <div className="mb-2.5 rounded-lg bg-[#F4F4F7] px-3 py-2 text-[10.5px] leading-snug text-neutral-700">
              <span className="font-semibold">Ranks 2 are shared.</span> Those jobs score identically and the engine cannot separate
              them — their order carries no meaning, treat them as one group. <span className="text-neutral-500">Straight off
              the server's own <span className="font-mono">ranking_note</span>. The engine refuses to claim an order it cannot
              justify, and the screen has to honour that refusal rather than print 1-2-3.</span>
            </div>
            <div className="flex flex-col gap-2">
              {COMPS.map((c) => <CompCard key={c.job} c={c} compact />)}
            </div>
          </CompsSection>
          <ComponentList />
        </Frame>

        {/* 4 ── EXPANDED ────────────────────────────────────────────────── */}
        <Frame
          step="4" state="open · expanded" title="Open one and read what it was made of" w={720}
          blurb="It expands in place, under its own card — the job block and the component list never leave the screen. High level first: assemblies. Click an assembly and its parts arrive, and not one moment before."
          server={<>
            The same rollup <span className="font-mono">_seed_components</span> uses, so what he reads is exactly what he would get:
            <span className="font-mono"> split_part(level,'.',1)</span>, each sub-assembly carrying the summed cost of its children.
            <span className="font-semibold"> An assembly whose parts all price at zero is SKIPPED</span> — so the header says
            <span className="italic"> 5 of 9 carried a cost</span> rather than quietly showing five.
          </>}
          human="Opens one, reads it, opens another, compares. Drills a row only when he wants the parts."
          recorded="Nothing. Reading is free and leaves no trace."
        >
          <JobBlock />
          <CompsSection state="open">
            <div className="flex flex-col gap-2">
              <CompCard c={COMPS[0]} open={openCard} onToggle={() => setOpenCard(!openCard)} />
              <CompCard c={COMPS[1]} compact />
            </div>
          </CompsSection>
        </Frame>

        {/* 5 ── COMPARE ─────────────────────────────────────────────────── */}
        <Frame
          step="5" state="open · compare" title="Two up, aligned by assembly"
          blurb="He is choosing at most two, so the mechanic should be the choice. Opening one, closing it and opening another asks him to compare across memory — which is exactly where an estimator's judgement gets worse, not better."
          server="Nothing new. Both bills of material are already loaded from step 4; this is a layout, not a request."
          human="Reads the same assembly across two jobs, side by side, with the difference stated rather than inferred."
          recorded="Nothing."
        >
          <Band n="2" tone="live">Comparable jobs — comparing two</Band>
          <SCard title="264714 MORPHE  ·  262919 L'ORÉAL" desc="Aligned by assembly. A row missing on one side is the finding, not a gap." action={<Illus />}>
            <table className="w-full">
              <thead>
                <tr className="[&_th]:pb-1.5 [&_th]:text-[9.5px] [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-neutral-600">
                  <th className="text-left">Assembly</th>
                  <th className="w-20 text-right">264714</th>
                  <th className="w-20 text-right">262919</th>
                  <th className="w-24 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {[["S1 Core — 2B CAN", "38.40", "31.05", "+23.7%"],
                  ["S1 left area, shelf + bracket", "22.15", "19.90", "+11.3%"],
                  ["S2 backstock dividers", "9.80", "—", "only on 264714"],
                  ["Back panels, printed", "17.25", "17.90", "−3.6%"],
                  ["Header, litho-mounted", "6.90", "6.90", "same"],
                  ["Drawer, lock and cover", "14.05", "—", "only on 264714"]].map(([n, a, b, d]) => (
                  <tr key={n} className="border-t border-neutral-100 [&_td]:py-1.5 [&_td]:text-[11px]">
                    <td className="text-neutral-800">{n}</td>
                    <td className="text-right tabular-nums text-neutral-900">{a === "—" ? <span className="text-neutral-300">—</span> : `$${a}`}</td>
                    <td className="text-right tabular-nums text-neutral-900">{b === "—" ? <span className="text-neutral-300">—</span> : `$${b}`}</td>
                    <td className={`text-right text-[10.5px] ${d.startsWith("only") ? "text-violet-700" : "text-neutral-500"}`}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2.5 text-[10.5px] leading-snug text-neutral-600">
              <span className="font-semibold">A comparable is worth its STRUCTURE, not its stored price.</span> Scott, asked how often
              the numbers get refreshed: <span className="italic">"They're custom every time… we're taking that fresh quote every
              time."</span> So this table is a shape he is choosing, and the money is re-sourced regardless.
            </p>
          </SCard>
        </Frame>

        {/* 6 ── CHOOSE ──────────────────────────────────────────────────── */}
        <Frame
          step="6" state="choosing" title="One button, and it names what it commits"
          blurb='Not "Done". The label says what will happen, because this is the moment exploring becomes the basis of an estimate.'
          server={<>
            <span className="font-mono">POST /ballparks/&#123;id&#125;/comps</span> → <span className="font-mono">ballpark.choose</span>.
            It <span className="font-semibold">REPLACES</span>: the previous comps and every row they seeded are deleted, and
            <span className="font-semibold"> rows the estimator typed survive</span> — <span className="font-mono">comp_job_id IS NULL</span>
            is his work, not ours. Then one row per costed assembly, stamped with <span className="font-mono">comp_job_id</span>.
          </>}
          human="Keeps two. Presses the button once."
          recorded={<>
            The chosen jobs and their rank. <Gap>the score at the time</Gap> <Gap>who chose, and when</Gap>
            {" "}<span className="font-mono">estimate_comps</span> has no actor and no timestamp column, and
            {" "}<span className="font-mono">choose</span> accepts an <span className="font-mono">actor</span> the API passes it and never
            uses. The most consequential judgement on this screen is stored anonymously.
          </>}
        >
          <Band n="2" tone="live">Comparable jobs</Band>
          <SCard
            title="Have we built this before?"
            desc="2 kept of 3 shown · 14 jobs on file"
            action={<Button size="sm" className="h-7 bg-neutral-900 px-3 text-[11px]">Use these two — seed the bill of material</Button>}
          >
            <div className="flex flex-col gap-2">
              <CompCard c={COMPS[0]} chosen compact />
              <CompCard c={COMPS[1]} chosen compact />
              <div className="rounded-2xl bg-[#F4F4F7] p-2.5 text-center text-[10.5px] text-neutral-500">
                243854 SEPHORA COLLECTION — excluded by you
              </div>
            </div>
            <div className="mt-2.5 flex items-center justify-between rounded-xl bg-[#F7F7F9] px-3 py-2">
              <span className="text-[10.5px] text-neutral-600">Or, if none of them fit —</span>
              <Button size="sm" variant="outline" className="h-6 border-neutral-300 px-2 text-[10.5px]">
                None of these are comparable
              </Button>
            </div>
          </SCard>
        </Frame>

        {/* 7 ── SETTLED ─────────────────────────────────────────────────── */}
        <Frame
          step="7" state="settled · used" title="It collapses to one line, and the list fills"
          blurb="The section does not go away — it shrinks to a sentence you can click. Underneath it, the component list he never typed, every row naming where it came from."
          server={<>
            One <span className="font-mono">estimate_lines</span> row per costed assembly, each carrying
            <span className="font-mono"> comp_job_id</span> and a source string. Two comparables merge and
            <span className="font-semibold"> every row says which one it came from</span>, so he corrects in the table rather than
            assigning aspects in the abstract before he has seen a single row.
          </>}
          human="Edits the list. Adds a row of his own — it is labelled as his, and a re-choose will not delete it."
          recorded={<>The comps, and the seeded rows. <span className="font-semibold">The sentence is DERIVED</span>, never stored: it counts <span className="font-mono">estimate_lines WHERE comp_job_id IS NOT NULL</span>, so deleting a seeded row changes it to <span className="italic">10 of 14</span> with no second write.</>}
        >
          <JobBlock />
          <CompsSection
            state="settled"
            sentence={<>
              Seeded from <span className="font-semibold">264714 Morphe</span> (0.94) and <span className="font-semibold">262919
              L'Oréal</span> (0.71) — <span className="font-semibold">6 of 7 rows</span> came from them.{" "}
              <button className="font-semibold text-sky-700 underline">Open</button>
            </>}
          />
          <ComponentList seeded />
        </Frame>

        {/* 8 ── THE THREE ENDINGS ───────────────────────────────────────── */}
        <Frame
          step="8" state="settled × 3" title="Three endings, and two of them look alike"
          blurb="This is the argument for the section never disappearing. Reviewed-five-and-kept-none and there-were-none-to-show are opposite facts about how far to trust the number — and if the section vanished, nothing afterwards could tell them apart."
          server={<>Only the third is the engine's own doing. <span className="font-semibold">Comparables are an ENRICHMENT, not a precondition</span> — measured: <span className="font-mono">kernel/bom · complexity · freight · labor · money · policy</span> contain <span className="font-semibold">zero</span> references to a comparable. The price never depended on one.</>}
          human="Keeps two · reviews five and keeps none · or is shown that there are none."
          recorded={<>All three. The middle one is a <span className="font-semibold">confidence driver</span> and the strongest signal in the whole interaction. <Gap>choosing nothing currently writes nothing at all</Gap></>}
        >
          {([
            ["settled · used", "Seeded from 264714 Morphe (0.94) and 262919 L'Oréal (0.71) — 6 of 7 rows came from them.", "emerald"],
            ["settled · none used", "Reviewed 5 comparable jobs; none used. This job is unlike anything on file.", "amber"],
            ["settled · none existed", "No comparable jobs on file. This price stands on the rate card and the complexity model — which is where it always stood.", "neutral"],
          ] as const).map(([st, sentence, tone]) => (
            <div key={st}>
              <div className="mb-1.5 flex items-center gap-2">
                <Badge variant="outline" className="border-neutral-400 bg-white px-1.5 py-0 font-mono text-[9.5px]">{st}</Badge>
              </div>
              <div className={`rounded-2xl bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] border-l-4 ${
                tone === "emerald" ? "border-l-emerald-500" : tone === "amber" ? "border-l-amber-500" : "border-l-neutral-400"
              }`}>
                <p className="text-[11.5px] leading-snug text-neutral-700">{sentence}</p>
              </div>
            </div>
          ))}
          <p className="px-1 text-[10.5px] leading-snug text-neutral-600">
            <span className="font-semibold">And the sentence goes onto the estimate itself.</span> That is how a reader a year later
            knows how much evidence the number stood on — which is the honesty an estimator has to see before he will trust any of this.
          </p>
        </Frame>

        {/* 9 ── THE RE-RUN ──────────────────────────────────────────────── */}
        <Frame
          step="9" state="detailed · re-run" title="The same section, later, with a better line to say"
          blurb="The detailed estimate does not re-run this blindly. It starts from what he already chose and re-runs because the input got richer — and if the answer moves, that is information, not noise."
          server="Same call, richer job: drawings, a real part list, quantities. The score changes because the evidence changed."
          human="Sees what his ballpark choice looks like now that the job is known, and decides whether to keep it."
          recorded="A second comparables decision on the detailed estimate — the ballpark's is not overwritten. Two moments, two records."
        >
          <Band n="2" tone="live">Comparable jobs — re-run</Band>
          <SCard title="Your ballpark stood on two jobs. One of them has moved." action={<Illus />}>
            <div className="flex flex-col gap-2">
              <div className="rounded-2xl bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] border-l-4 border-l-emerald-500">
                <p className="text-[11.5px] text-neutral-800">
                  <span className="font-mono font-bold">264714</span> MORPHE — <span className="font-semibold">0.94 → 0.96</span>, still your closest.
                </p>
                <p className="mt-0.5 text-[10.5px] text-neutral-600">The drawings confirmed the drawer assembly you both share.</p>
              </div>
              <div className="rounded-2xl bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] border-l-4 border-l-amber-500">
                <p className="text-[11.5px] text-neutral-800">
                  <span className="font-mono font-bold">262919</span> L'ORÉAL — <span className="font-semibold">0.71 → rank 6</span>.
                </p>
                <p className="mt-0.5 text-[10.5px] text-neutral-600">
                  Your real quantity came in at 120, not 400. It seeded 2 of your 7 rows — those rows are still there and still labelled.
                </p>
              </div>
            </div>
            <p className="mt-2.5 text-[10.5px] leading-snug text-neutral-600">
              <span className="font-semibold">This is the moment an estimator starts trusting the tool</span> — it told him his earlier
              evidence was thinner than he thought, unprompted, with the number to prove it.
            </p>
          </SCard>
        </Frame>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE BALLPARK PAGE — IN THE SHIPPED DESIGN, WITH THE COMPARABLES IN IT
// ═══════════════════════════════════════════════════════════════════════════
//
// Erez, 2 Sept, twice: "you're using the old design… please use the design of
// the detailed estimate input page."
//
// ⛔ HE WAS RIGHT AND THE MISTAKE IS WORTH NAMING. I copied
// `command/outform-input-ux.tsx` — Jarvis's board design, two columns and a
// 400px agent rail — because it was the design I had already read. The design
// that actually ships is `tailormind-outform-console/web/src/outform/float-ui.tsx`,
// and chapter 26 says so in its own first line: *"Every control below is the one
// the ballpark already uses — 36px tall, 11.2px corners, gap-2 under the label,
// three even columns, white cards on the wash, numbered bands, nothing under a
// field except a reason."* I had read that sentence and built something else.
//
// So every token below is COPIED FROM `float-ui.tsx`, verbatim, not approximated:
//
//   LIFT   shadow-[0_1px_3px_rgba(16,24,40,0.05)]   the hairline, not a spread
//   CARD   rounded-[22px] bg-white + LIFT           the lift is the white plane on a coloured page
//   WELL   rounded-2xl bg-[#F4F4F7]                 a filled region INSIDE a card
//   CTL    h-9 rounded-xl bg-[#F4F4F7] border-transparent, and NOTHING on hover
//          or focus — Erez: "I don't want any changes on hover or selected."
//   THEAD  h-10 · px-2 · 12px · font-medium · neutral-500 · SENTENCE CASE · nowrap
//          ⚠️ Sentence case REVERSES the old house rule on purpose: uppercase
//          with tracking makes a string ~20% wider, and "MATERIAL & FINISH"
//          then needs two lines where "Material & Finish" needs one. The
//          styling WAS the wrapping.
//   Field  grid gap-2, label leading-none, and the row alignment bought by
//          `grid-rows-subgrid` rather than by a reserved label height.
//
// It is copied rather than imported because that file lives in a different
// repository — the client's console — and a board cannot import from it.
// ⛔ If float-ui.tsx changes, this drifts. It is a MOCK of that design, not a
// second source of it, and it says so on screen.
//
// ⭐ AND ONE THING THE SHIPPED SURFACE DOES THAT THIS DOES NOT: `Surface` lays
// out TWO columns plus the agent rail. The chapter-26 mock is a single
// full-width column of bands, which is what makes its three-column field grids
// read, so this follows the MOCK — same controls, same cards, same bands.

const F_LIFT = "shadow-[0_1px_3px_rgba(16,24,40,0.05)]";
const F_CARD = `rounded-[22px] bg-white ${F_LIFT}`;
const F_WELL = "rounded-2xl bg-[#F4F4F7]";
/** The control. One height, one radius, and static — no hover, no focus move. */
const F_CTL = "flex h-9 w-full items-center rounded-xl border border-transparent bg-[#F4F4F7] px-3 text-[13px] text-neutral-900";
const F_THEAD = "[&_th]:h-10 [&_th]:border-0 [&_th]:px-2 [&_th]:align-middle [&_th]:whitespace-nowrap [&_th]:text-[12px] [&_th]:font-medium [&_th]:text-neutral-500 [&_th]:text-left";

/** float-ui `Band` — the numbered section head, OUTSIDE the card. */
function FBand({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-1 first:pt-0">
      <span className="flex size-5 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-bold text-white">{n}</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-600">{children}</span>
    </div>
  );
}

/** float-ui `FCard`. Title optional: a card under a band takes its name from the band. */
function FCard({ title, desc, action, tone = "plain", className = "", children }: {
  title?: string; desc?: React.ReactNode; action?: React.ReactNode;
  tone?: "plain" | "sunk"; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`rounded-[22px] ${tone === "sunk" ? "bg-[#F4F4F7]" : "bg-white"} ${F_LIFT} ${className}`}>
      {title || desc || action ? (
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3">
          <div className="min-w-0">
            {title ? <h3 className="text-[17px] font-semibold leading-tight tracking-[-0.01em] text-neutral-900">{title}</h3> : null}
            {desc ? <p className="mt-1 text-[12px] leading-snug text-neutral-500">{desc}</p> : null}
          </div>
          {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
      ) : null}
      <div className={`px-6 pb-6 ${title || desc || action ? "" : "pt-6"}`}>{children}</div>
    </div>
  );
}

/** float-ui `Field` + `FieldGrid` — the subgrid alignment, not a reserved height. */
function FGrid({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`grid grid-cols-3 auto-rows-auto gap-x-5 gap-y-4 ${className}`}
         style={{ gridTemplateRows: "repeat(2, auto)", gridAutoRows: "auto auto" }}>
      {children}
    </div>
  );
}
function FField({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="row-span-2 grid grid-rows-subgrid gap-2">
      <label className="text-[12.5px] font-semibold leading-none text-neutral-800">{label}</label>
      <div className="flex flex-col gap-1">
        {children}
        {hint ? <p className="text-[10.5px] leading-tight text-neutral-500">{hint}</p> : null}
      </div>
    </div>
  );
}
function FVal({ v, muted = false, caret = false }: { v?: string; muted?: boolean; caret?: boolean }) {
  return (
    <div className={`${F_CTL} justify-between`}>
      <span className={muted ? "text-neutral-400" : ""}>{v}</span>
      {caret ? <ChevronDown className="size-3.5 shrink-0 text-neutral-400" /> : null}
    </div>
  );
}

/** float-ui `PlainNote` — an ordinary sentence, deliberately NOT amber and no icon. */
function FNote({ children }: { children: React.ReactNode }) {
  return <p className="rounded-2xl bg-[#F4F4F7] px-4 py-3 text-[12px] leading-snug text-neutral-600">{children}</p>;
}
/** The amber strip from the mock — for a real caution, never for an absence. */
function FWarn({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-2xl bg-amber-50 px-4 py-3 text-[12px] leading-snug text-amber-950">
      <TriangleAlert className="mr-1.5 inline size-3.5 -translate-y-px" />{children}
    </p>
  );
}
/** float-ui `Stat` / `StatBar` — the derived tiles under the typed numbers. */
function FStatBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-[#F1F1F5] shadow-[0_1px_3px_rgba(16,24,40,0.05)]">
      <div className="flex items-stretch divide-x divide-neutral-200/70">{children}</div>
    </div>
  );
}
function FStat({ label, children, note }: { label: string; children: React.ReactNode; note?: string }) {
  return (
    <div className="min-w-0 flex-1 px-4 py-3">
      <p className="truncate text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500">{label}</p>
      <p className="mt-1 truncate text-[19px] font-semibold leading-tight tabular-nums text-neutral-900">{children}</p>
      {note ? <p className="mt-0.5 truncate text-[10.5px] leading-snug text-neutral-500">{note}</p> : null}
    </div>
  );
}
function FIllus({ children = "illustrative" }: { children?: React.ReactNode }) {
  return (
    <Badge variant="outline" className="border-transparent bg-amber-100 text-[10px] font-semibold text-amber-900">
      <TriangleAlert className="size-3" />{children}
    </Badge>
  );
}
/** A thing the code does not do yet. Absent, never omitted. */
function FGap({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-dashed border-rose-300 bg-rose-50 px-1.5 py-[1px] text-[10px] font-semibold text-rose-800">
      not built · {children}
    </span>
  );
}

// ── the comparable, IN THE DOCUMENT'S OWN VOCABULARY ───────────────────────
//
// ⛔ THIS IS THE THIRD CORRECTION AND IT IS THE ONE THAT NAMES THE HABIT.
// Erez, 2 Sept: "you need to change the design of the comparable section to be
// the same design as the top section… why do you keep doing it with the old
// design?"
//
// The first two corrections were about WHICH FILE to copy. This one is about
// something narrower and worse: I copied the right tokens for bands 1 and 3 —
// the fields, the tables, the tiles — and then invented a bespoke vocabulary
// for band 2. Grey sunk cards with a ring, pill chips, inline amber badges and
// three button styles that appear nowhere else in the document. Every one of
// those was mine. **Matching a design system is not matching its colours; it is
// refusing to add a component it does not have.**
//
// The document has exactly four ways of showing anything:
//     a FIELD GRID     — three even columns of labelled controls
//     a TABLE          — thin sentence-case heads, plain rows, a toggle for a
//                        boolean, and a small grey footnote underneath
//     a STAT BAR       — derived numbers as tiles, under the thing they derive from
//     a NOTE           — grey for an ordinary sentence, amber for a real caution
//
// So the comparables are a TABLE. Keep is a TOGGLE, exactly as Include is a
// toggle on the bill of materials — same control, same meaning: the row stays
// on the record and drops out of the total. The reasons a job matched, and the
// way it differs, belong to the row you opened, so they sit in the panel under
// it with its bill of material. Nothing new was invented for this band.

/** The document's toggle — the same one `Include` uses on the BOM. */
function FToggle({ on }: { on: boolean }) {
  return (
    <span className={`inline-flex h-[22px] w-[38px] items-center rounded-full px-[3px] ${on ? "justify-end bg-neutral-900" : "justify-start bg-neutral-300"}`}>
      <span className="size-4 rounded-full bg-white" />
    </span>
  );
}

/** One comparable's assemblies. `ev` is EVIDENCE, not a win rate — see the
 *  warning under the table for why those are different things. ⚠️ Illustrative
 *  values; the two recurrence figures quoted beside them are measured. */
const COMP_BOM: { n: string; name: string; parts: number; ext: string; ev: "strong" | "fair" | "thin"; basis: string; take: boolean }[] = [
  { n: "1", name: "S1 Core — 2B CAN", parts: 16, ext: "38.40", ev: "strong", basis: "3 processes, all recurring · Mar 2025", take: true },
  { n: "2", name: "S1 left area, shelf + bracket", parts: 9, ext: "22.15", ev: "fair", basis: "2 of 3 processes recur · Mar 2025", take: true },
  { n: "3", name: "S2 backstock dividers", parts: 4, ext: "9.80", ev: "strong", basis: "die-cut + assembly, both common", take: true },
  { n: "4", name: "Drawer, lock and cover", parts: 7, ext: "14.05", ev: "thin", basis: "one occurrence · vendor unmatched to D365", take: false },
  { n: "5", name: "Back panels, printed", parts: 3, ext: "17.25", ev: "fair", basis: "direct print recurs · cost 18 months old", take: true },
];

const FULL_BOM = [
  { i: 1, d: "S1 Core — 2B CAN", m: "Styrene / Sintra with magnets", src: "PPF", q: 1, c: 38.4, from: "264714" },
  { i: 2, d: "S1 left area, shelf + bracket", m: "Acrylic, brushed gold finish", src: "PPF", q: 1, c: 22.15, from: "264714" },
  { i: 3, d: "S2 backstock dividers", m: "Styrene, direct print", src: "UNC", q: 2, c: 9.8, from: "264714" },
  { i: 4, d: "Back panels, printed", m: "Sintra 6mm, directprint graphic", src: "CHI", q: 1, c: 17.25, from: "262919" },
  { i: 5, d: "Header, litho-mounted 24pt", m: "Litho on E-flute", src: "CHI", q: 1, c: 6.9, from: "262919" },
  { i: 6, d: "Drawer, lock and cover", m: "ABS, magnetic attachment", src: "PPF", q: 1, c: 14.05, from: "264714" },
  { i: 7, d: "Mirror panel, polished edge", m: "3mm mirror acrylic", src: "", q: 1, c: 6.2, from: null },
];

export function BallparkWithComparables() {
  const [open, setOpen] = useState<string | null>("264714");
  const kept = ["264714", "262919"];
  const total = FULL_BOM.reduce((s, r) => s + r.q * r.c, 0);
  const seeded = FULL_BOM.filter((r) => r.from).length;

  return (
    <div className="nopan nodrag w-[1320px] text-neutral-900" onWheelCapture={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-4">

        {/* ── 1 · THE JOB ─────────────────────────────────────────────── */}
        <FBand n="1">The job</FBand>
        <FCard>
          <FGrid>
            <FField label="Customer" hint="a picker over their 890 sourced clients — never free text, because free text is how one client gets spelled four ways and comparable ranking stops matching it">
              <FVal v="MORPHE" caret />
            </FField>
            <FField label="Project Description"><FVal v="Sephora 2Bay SPR'27 Overhaul" /></FField>
            <FField label="Project Number"><FVal v="265314" /></FField>
            <FField label="Commercial Lead"><FVal v="Sage Turowski" /></FField>
            <FField label="APM"><FVal v="Alison Chepela" /></FField>
            <FField label="Vertical" hint="Other!H5:H9 — five options, and Beauty is what gates the Hotline recovery">
              <FVal v="Beauty" caret />
            </FField>
            <FField label="Client Budget / Unit"><FVal v="500.00" /></FField>
            <FField label="Market Comp / Unit"><FVal v="545.00" /></FField>
            <div />
          </FGrid>
        </FCard>

        {/* ── 2 · COMPARABLE JOBS — the band that changes ──────────────── */}
        <FBand n="2">Comparable jobs</FBand>
        <FCard
          desc="It searched on its own the moment the customer and the description existed — there is no Search button. Keep is a toggle, never a delete: the job stays on the record and stops seeding."
          action={
            <button className="inline-flex h-9 items-center rounded-xl bg-neutral-900 px-3.5 text-[12.5px] font-medium text-white">
              Use these {kept.length} — seed the bill of material
            </button>
          }
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-[#FAFAFB]">
            <table className="w-full caption-bottom text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-20">Job #</th><th>Client</th><th>Description</th>
                  <th className="w-24">Built</th>
                  <th className="w-20 !text-right">Score</th><th className="w-16 !text-right">Rank</th>
                  <th className="w-28 !text-right">Historical cost</th><th className="w-16 !text-right">Units</th>
                  <th className="w-24 !text-right">CM</th><th className="w-16 !text-right">Keep</th>
                </tr>
              </thead>
              <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                {COMPS.map((c) => {
                  const on = kept.includes(c.job);
                  return (
                    <tr key={c.job} onClick={() => setOpen(open === c.job ? null : c.job)} className="cursor-pointer">
                      <td className="font-mono text-neutral-600">{c.job}</td>
                      <td className="font-medium text-neutral-900">{c.client}</td>
                      <td className="text-neutral-600">{c.what}</td>
                      <td className="text-neutral-500">{c.when}</td>
                      <td className="text-right font-semibold tabular-nums">{c.score}</td>
                      <td className="text-right tabular-nums text-neutral-500">{c.rank}</td>
                      <td className="text-right tabular-nums">{c.cost}</td>
                      <td className="text-right tabular-nums text-neutral-600">{c.units}</td>
                      {/* ⛔ THE ONLY AMBER IN THE TABLE, AND IT EARNS IT. Outcome is
                          mastered in Sugar and Sugar is not connected, so every one of
                          the fourteen jobs has a NULL outcome and this figure is
                          fabricated. It travels with its mark or it does not travel. */}
                      <td className="text-right">
                        <span className="rounded-md bg-amber-100 px-1.5 py-[2px] text-[11.5px] font-medium tabular-nums text-amber-900">{c.cm}</span>
                      </td>
                      <td className="text-right"><FToggle on={on} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-2 px-1 text-[11px] leading-snug text-neutral-500">
            <span className="font-medium text-neutral-700">Ranks 2 are shared.</span> Those jobs score identically and the engine
            cannot separate them — their order carries no meaning, treat them as one group. That is the server's own
            <span className="font-mono"> ranking_note</span>: it refuses to claim an order it cannot justify, and the screen
            honours the refusal rather than printing 1-2-3. <span className="font-medium text-neutral-700">Every CM above is a
            placeholder</span> — outcome is mastered in Sugar, which is not connected, so no job on file carries one.
            Click a row to read why it matched and what it was made of.
          </p>

          {open ? (() => {
            const c = COMPS.find((x) => x.job === open)!;
            return (
              <div className="mt-4 rounded-2xl bg-[#F4F4F7] p-4">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-[13px] font-semibold text-neutral-900">
                    <span className="font-mono font-medium text-neutral-500">{c.job}</span> {c.client} — {c.what}
                  </p>
                  <span className="text-[11.5px] text-neutral-500">5 of 9 assemblies carried a cost · 4 skipped, all parts at zero</span>
                </div>

                <FGrid className="mt-3">
                  <FField label="Why it matched">
                    <div className="flex min-h-9 flex-col justify-center gap-0.5 rounded-xl bg-white px-3 py-2 text-[12px] leading-snug text-neutral-700">
                      {c.matched.map((m) => <span key={m}>{m}</span>)}
                    </div>
                  </FField>
                  <FField label="How it differs">
                    <div className="flex min-h-9 items-center rounded-xl bg-white px-3 py-2 text-[12px] leading-snug text-neutral-700">
                      {c.differs}
                    </div>
                  </FField>
                  <FField label="What it would seed">
                    <div className="flex min-h-9 items-center rounded-xl bg-white px-3 py-2 text-[12px] leading-snug text-neutral-700">
                      The four rows you ticked below, each stamped with this job number. Untick one and it does not arrive.
                      Rows you typed yourself are never touched by any of this.
                    </div>
                  </FField>
                </FGrid>

                {/* ⭐ TAKE PER LINE, AND A SCORE PER LINE — the thing the Data
                    Pilot gestured at and never built.
                    Erez, 2 Sept: "when we select a comparable job and we open it
                    to see its bill of material, we should be able to select that
                    we want this specific item in the ballpark. And each item
                    needs to have a win score next to it."

                    THEIR PRODUCT HAS THREE GRANULARITIES AND ONLY JOINS TWO:
                      job      — the card: similarity %, won/lost, the CM achieved
                      CATEGORY — "EVIDENCE USED", one row per cost category, each
                                 a DROPDOWN naming which past job it borrows from
                      LINE     — the component breakdown carries a source BADGE,
                                 read-only, on 2 of its 8 rows, with no control
                                 and no score
                    So nobody can say "take THIS item from THIS job". That is the
                    gap, and it is exactly where an estimator's expertise lives.

                    ⛔ AND THEIR PER-CATEGORY "MATCH" COLUMN IS NOT A PER-ITEM
                    SCORE. Read off their own screen: 84% appears on FOUR
                    categories, all sourced from Samsung, and 89% on the one
                    sourced from TCL. It is the WHOLE-JOB similarity, copied down
                    a column that looks like per-item evidence. A number that
                    changes granularity without changing value is decoration. */}
                <div className="mt-3 relative w-full overflow-x-auto rounded-2xl bg-white">
                  <table className="w-full caption-bottom text-[13px]">
                    <thead className={F_THEAD}>
                      <tr>
                        <th className="w-10">#</th><th>Assembly</th>
                        <th className="w-16 !text-right">Parts</th>
                        <th className="w-24">Evidence</th>
                        <th className="w-48">What it stands on</th>
                        <th className="w-28 !text-right">Extended</th>
                        <th className="w-16 !text-right">Take</th>
                      </tr>
                    </thead>
                    <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                      {COMP_BOM.map((r) => (
                        <tr key={r.n}>
                          <td className="text-neutral-500">{r.n}</td>
                          <td className="text-neutral-800">{r.name}</td>
                          <td className="text-right tabular-nums text-neutral-500">{r.parts}</td>
                          <td>
                            <span className={`rounded-md px-2 py-[2px] text-[11.5px] font-medium ${
                              r.ev === "strong" ? "bg-neutral-900 text-white"
                              : r.ev === "fair" ? "bg-[#F4F4F7] text-neutral-700"
                              : "bg-amber-100 text-amber-900"}`}>{r.ev}</span>
                          </td>
                          <td className="text-[11.5px] text-neutral-500">{r.basis}</td>
                          <td className="text-right font-semibold tabular-nums">${r.ext}</td>
                          <td className="text-right"><FToggle on={r.take} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 px-1 text-[11px] leading-snug text-neutral-500">
                  <span className="font-medium text-neutral-700">Take is a toggle, and it is the point of opening this.</span>{" "}
                  Four of these five rows go into the bill of material below; the drawer does not, because you have one on this
                  job that is nothing like theirs. Rolled to assembly exactly as <span className="font-mono">_seed_components</span>{" "}
                  does it, so what you read is what you would get — and an assembly whose parts all price at zero is skipped
                  rather than shown as a row of noise, which is why the count is named.
                </p>

                <FWarn>
                  <span className="font-semibold">You asked for a win score per item, and there is no honest one — so this column
                  is not called that.</span> A win is a property of a JOB, not of a line: a customer accepts a price, never a
                  drawer. And we hold no outcome at all — it is mastered in Sugar, Sugar is not connected, and all fourteen jobs
                  have it NULL. <span className="font-semibold">Evidence is what a line can honestly carry</span>, and it is
                  computed from things we do hold: whether the assembly&rsquo;s processes recur across the corpus, how old the
                  cost is, and whether its vendor resolves to their D365 master.{" "}
                  <FGap>the evidence score itself — nothing computes it yet</FGap>{" "}
                  ⚠ Measured, and it is the constraint on this whole column: <span className="font-mono">part_no</span> has 160
                  distinct values and <span className="font-semibold">0 recur</span>, so there is no signal at part level —
                  but <span className="font-mono">process</span> has 22 with <span className="font-semibold">10 recurring</span>,
                  which is why the score is computed over an assembly&rsquo;s processes and not its parts.
                </FWarn>
              </div>
            );
          })() : null}

          <div className="mt-4">
            <FStatBar>
              <FStat label="On file" note="the whole corpus">14</FStat>
              <FStat label="In this vertical" note="Beauty">5</FStat>
              <FStat label="Shown" note="closest by attribute">3</FStat>
              <FStat label="Kept" note="two of them tie at rank 2">{kept.length}</FStat>
              <FStat label="Rows it will seed" note="ticked line by line, not whole jobs">6 of 8</FStat>
            </FStatBar>
          </div>

          <FWarn>
            <span className="font-semibold">Keeping none is a different fact from there being none</span>, and only one of them is
            recorded today. Reviewing three and keeping none says this job is unlike anything on file — that belongs on the
            estimate and in the confidence score. Not built yet:{" "}
            <FGap>choosing nothing writes nothing at all</FGap> <FGap>the score is not stored with the choice</FGap>{" "}
            <FGap>no actor and no timestamp on the row</FGap> The scoring, the shared rank and the seeding all run today. MJOS-246.
          </FWarn>
        </FCard>

        {/* ── 3 · BILL OF MATERIALS ───────────────────────────────────── */}
        <FBand n="3">Bill of materials</FBand>
        <FCard
          desc="Their eight columns from 264511 BALLPARK TEMPLATE. Assembly level — around twenty rows, never 389 parts."
          action={<div className="flex items-center gap-2"><FIllus />
            <button className="inline-flex h-9 items-center gap-1 rounded-xl bg-[#F4F4F7] px-3 text-[12.5px] font-medium text-neutral-800">
              <Plus className="size-3.5" /> Add row
            </button></div>}
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-[#FAFAFB]">
            <table className="w-full caption-bottom text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-12">Item</th><th>Description</th><th className="w-14">Image</th>
                  <th>Material &amp; Finish</th><th className="w-24">Production Source</th>
                  <th className="w-14 !text-right">Qty</th><th className="w-28 !text-right">Unit cost (EXW)</th>
                  <th className="w-28 !text-right">Ext unit cost</th><th className="w-28">Origin</th>
                </tr>
              </thead>
              <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                {FULL_BOM.map((r) => (
                  <tr key={r.i}>
                    <td className="text-neutral-500">{r.i}</td>
                    <td className="text-neutral-900">{r.d}</td>
                    <td><div className="size-7 rounded-md bg-[#F0F0F3]" /></td>
                    <td className="text-neutral-600">{r.m}</td>
                    <td className="font-mono text-[12px] text-neutral-600">{r.src || <span className="text-neutral-400">—</span>}</td>
                    <td className="text-right tabular-nums text-neutral-700">{r.q}</td>
                    <td className="text-right tabular-nums">${r.c.toFixed(2)}</td>
                    <td className="text-right font-semibold tabular-nums">${(r.q * r.c).toFixed(2)}</td>
                    <td>
                      {/* ⛔ THE ONLY BLACK ON THIS PAGE IS THE BAND NUMBER. A
                          black badge here made the estimator's own row the
                          loudest thing in the table, which is backwards — his
                          row is the ordinary case and ours is the one carrying
                          a claim. Same chip, weight tells them apart. */}
                      <span className={`rounded-md bg-white px-2 py-[2px] text-[11px] ${
                        r.from ? "font-medium text-neutral-600" : "font-semibold text-neutral-900"}`}>
                        {r.from ? `from ${r.from}` : "typed by you"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <FStatBar>
              <FStat label="Net component cost">${total.toFixed(2)}</FStat>
              <FStat label="Rows" note="assemblies, not parts">{FULL_BOM.length}</FStat>
              <FStat label="Seeded" note="from the two comparables you kept">{seeded} of {FULL_BOM.length}</FStat>
              <FStat label="Typed by you" note="a re-choose deletes ours, never yours">{FULL_BOM.length - seeded}</FStat>
            </FStatBar>
          </div>

          <p className="mt-3 text-[11px] leading-snug text-neutral-500">
            <span className="font-medium text-neutral-700">Ext unit cost is computed — Qty × Unit cost — and never typed.</span>{" "}
            Two of their eleven columns are deliberately absent: <span className="font-mono">QTY per Container</span> reads
            <span className="font-mono"> 9999999</span> on every row of their own file, a placeholder meaning <i>not known</i>,
            and <span className="font-mono">Freight Est</span> is <span className="font-mono">=#REF!/J</span>, broken on every
            row. A column their own estimator never filled is not a requirement we discovered; it is a field we would be
            inventing. The cube-and-container arithmetic is real and belongs to the detailed estimate.
          </p>
        </FCard>

        <FNote>
          Complexity, the six unit populations and the range are not on this screen. Erez, 2 September: <i>"complexity is out —
          the ballpark is only what I told you."</i> The range is output and lives on the Estimate tab. ⚠ The engine still needs
          the rating — it is <span className="font-mono">$D$3</span> and all sixteen rate formulas read it — so it must be
          derived or read, never defaulted.
        </FNote>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE TWO MISSING SECTIONS — BOTTOM UP BUILD + BREAKOUT
// ═══════════════════════════════════════════════════════════════════════════
//
// Erez, 2 Sept: "we already decided that we need to add the two tabs, the
// bottom up and the breakout. Add them to the design." And: "make sure that in
// the design you put all the business logic that you found in your analysis so
// I can see it — it needs to be a drop-down, a switch, a button, whatever it
// is. I want to see the business logic."
//
// ⛔ SO EVERY RULE FOUND IN EIGHT CYCLES OF READING THE THREE WORKBOOKS IS ON
// SCREEN AS A CONTROL OR AS A STATED BASIS — never as prose beside the table.
// Where their sheet hides a rule in a formula, this shows it as the thing it is.
//
// ⚠️ These are rendered BESIDE Jarvis's `DetailedInputUX`, not inside it. That
// file is his and it is untracked; a board has one owner. The skin is the
// console's own `float-ui.tsx`, so the three read as one page.

const POPS = [
  { col: "I", name: "Units Sold", units: 115, cost: "145.9058" },
  { col: "K", name: "R&M / Overs up to Max", units: 12, cost: "252.6650" },
  { col: "M", name: "R&M / Overs > Max", units: 0, cost: "149.8796" },
  { col: "P", name: "Total Units Estimated for Pricing", units: 127, cost: "156.1947" },
  { col: "T", name: "NSO Units — not in Price", units: 0, cost: "144.8396" },
  { col: "W", name: "Total Units", units: 127, cost: "156.1947" },
];

/** One row of the engine. `qty` is EDITABLE only where the workbook unlocks it. */
type BURow = {
  cell: string; label: string; qty: string; qtyFrom?: string; open?: boolean;
  source: string; rate?: string; amount: string; gate?: string;
};

const TIER_RAW: BURow[] = [
  { cell: "11", label: "Material / Component Cost", qty: "1", open: true, source: "SUM(BOM!J:J)", amount: "66.32" },
  { cell: "12", label: "Labor", qty: "1", open: true, source: "Labor!H62 × Labor!F4 ÷ W7", amount: "12.84" },
  { cell: "16", label: "BUBBLEWRAP", qty: "4", qtyFrom: "Other Costs r4", source: "Other Costs!G4", amount: "0.88" },
  { cell: "17", label: "CARTON (Stock Beauty)", qty: "1", qtyFrom: "Other Costs r5", source: "Other Costs!G5", amount: "7.77" },
  { cell: "18", label: "POLYBAG", qty: "1", qtyFrom: "Other Costs r6", source: "Other Costs!G6", amount: "0.42" },
];
const TIER_FREIGHT: BURow[] = [
  { cell: "37", label: "Freight Costs", qty: "1", open: true, source: "Freight!O17", amount: "9.40" },
  { cell: "38", label: "Tariff", qty: "1", open: true, source: "Freight!O20", amount: "0.99" },
  { cell: "39", label: "Duties", qty: "1", open: true, source: "Freight!O21", amount: "0.28" },
  { cell: "40", label: "Inspection", qty: "1", open: true, source: "Freight!O22", amount: "—" },
  { cell: "41", label: "Processing", qty: "1", open: true, source: "Freight!O23", amount: "—" },
  { cell: "42", label: "Harbor Maintenance Fee", qty: "1", open: true, source: "Freight!O24", amount: "—" },
];
const TIER_HARD: BURow[] = [
  { cell: "55", label: "Instruction Sheet Development", qty: "1", source: "Assumptions r10", rate: "$3.20", amount: "3.20" },
  { cell: "57", label: "Pre-Flight / Pre-Press Graphic Dev", qty: "—", source: "Assumptions r11", rate: "1.0%", amount: "0.66" },
  { cell: "58", label: "Design", qty: "—", source: "Assumptions r12", rate: "3.0%", amount: "1.99" },
  { cell: "59", label: "Engineering", qty: "—", source: "Assumptions r13", rate: "2.75%", amount: "1.82" },
  { cell: "60", label: "Quality Assurance", qty: "—", source: "Assumptions r14", rate: "1.5%", amount: "0.99" },
];
const TIER_OTHER: BURow[] = [
  { cell: "64", label: "Price Cushion", qty: "—", source: "Assumptions r17", rate: "2.0%", amount: "1.33" },
  { cell: "66", label: "Labor Cushion", qty: "—", source: "Assumptions r18", rate: "2.0%", amount: "0.26" },
  { cell: "67", label: "Scrap Factor Applied", qty: "—", source: "Assumptions r19", rate: "4.0%", amount: "2.65" },
  { cell: "68", label: "Freight Factor", qty: "—", source: "Assumptions r20", rate: "3.0%", amount: "0.32" },
  { cell: "69", label: "Hotline — Beauty Only", qty: "—", source: "Assumptions r21", rate: "1.5%", amount: "0.99", gate: "vertical = Beauty" },
  { cell: "70", label: "Call Center — Non-Beauty", qty: "—", source: "Assumptions r22", rate: "1.0%", amount: "—", gate: "only if Hotline is 0" },
  { cell: "71", label: "SG&A Recovery", qty: "—", source: "Assumptions r23", rate: "30%", amount: "19.90" },
  { cell: "72", label: "Tech Specific Recovery", qty: "—", source: "Assumptions r24", rate: "2.5%", amount: "—", gate: "Retail Media = N → zeroed" },
];

function BURows({ rows }: { rows: BURow[] }) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.cell} className={r.amount === "—" ? "opacity-55" : ""}>
          <td className="font-mono text-[11px] text-neutral-400">E{r.cell}</td>
          <td className="text-neutral-900">{r.label}</td>
          <td>
            {r.open ? (
              <span className="inline-flex h-7 w-14 items-center justify-center rounded-lg bg-white ring-1 ring-neutral-900/70 text-[12px] font-semibold tabular-nums">
                {r.qty}
              </span>
            ) : r.qtyFrom ? (
              <span className="text-[11.5px] tabular-nums text-neutral-500">{r.qty}<span className="ml-1.5 text-neutral-400">← {r.qtyFrom}</span></span>
            ) : <span className="text-neutral-300">—</span>}
          </td>
          <td className="font-mono text-[11px] text-neutral-500">{r.source}</td>
          <td>
            {r.rate ? (
              <span className="rounded-md bg-[#F4F4F7] px-2 py-[2px] text-[11.5px] font-medium tabular-nums text-neutral-700">{r.rate}</span>
            ) : <span className="text-neutral-300">—</span>}
          </td>
          <td className="text-[11px] text-neutral-500">{r.gate ?? ""}</td>
          <td className="text-right font-semibold tabular-nums">{r.amount === "—" ? <span className="text-neutral-300">—</span> : `$${r.amount}`}</td>
        </tr>
      ))}
    </>
  );
}

function TierHead({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <tr><td colSpan={7} className="!pt-4 !pb-1">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-neutral-500">{n} · {children}</span>
    </td></tr>
  );
}

/** The nine-point ladder, rendered as the HEAT-GRADED STRIP their sheet uses —
 *  because the estimator picks by reading where the colour turns, not by
 *  comparing nine numbers. Their only two conditional formats do exactly this. */
function Ladder({ label, values, chosen, inert, fmt = (v: number) => `$${v.toFixed(0)}` }: {
  label: string; values: (number | null)[]; chosen?: number; inert?: boolean; fmt?: (v: number) => string;
}) {
  const nums = values.filter((v): v is number => v !== null);
  const lo = Math.min(...nums), hi = Math.max(...nums);
  return (
    <tr>
      <td className="whitespace-nowrap pr-3 text-[12px] font-medium text-neutral-700">{label}</td>
      {values.map((v, i) => {
        if (inert || v === null) {
          return <td key={i} className="px-1 py-1 text-center text-[11px] text-rose-700">#DIV/0!</td>;
        }
        const t = hi === lo ? 0.5 : (v - lo) / (hi - lo);
        const bg = t < 0.5
          ? `rgba(248,113,113,${0.55 - t * 0.5})`
          : `rgba(74,222,128,${(t - 0.5) * 0.75 + 0.12})`;
        return (
          <td key={i} className="px-1 py-1">
            <div className={`rounded-md px-2 py-1.5 text-center text-[11.5px] tabular-nums ${i === chosen ? "ring-2 ring-neutral-900 font-bold" : ""}`}
                 style={{ background: bg }}>
              {fmt(v)}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

export function BottomUpAndBreakout() {
  const [rating, setRating] = useState(2);
  const [pvOverride, setPvOverride] = useState(false);
  const [retailMedia, setRetailMedia] = useState(false);
  const [vertical, setVertical] = useState("Beauty");
  const [pop, setPop] = useState("P");
  const [chosen, setChosen] = useState(3);
  const [budgetSet, setBudgetSet] = useState(false);

  const MARGINS = [0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75];
  const cost = 156.19;
  const price = MARGINS.map((m) => cost / (1 - m));
  const profit = price.map((p) => p - cost);
  const active = POPS.find((p) => p.col === pop) ?? POPS[3];

  return (
    <div className="nopan nodrag w-[1620px] text-neutral-900" onWheelCapture={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-4">

        {/* ── 5 · BOTTOM-UP BUILD ─────────────────────────────────────── */}
        <FBand n="5">Bottom-up build</FBand>

        {/* THE CONTROL STRIP — every rule that moves the whole sheet, as a control */}
        <FCard
          title="What moves every number below"
          desc="Four controls. Between them they retime sixteen rates, switch two recovery lines on and off, and apply or withhold a company-wide discount."
        >
          <FGrid>
            <FField label="Complexity rating — $D$3"
                    hint="Selects column F, H or J in Assumptions. ONE number, and all sixteen rates move together.">
              <div className="flex gap-1.5">
                {[1, 2, 3].map((n) => (
                  <button key={n} onClick={() => setRating(n)}
                    className={`h-9 flex-1 rounded-xl text-[13px] font-semibold ${
                      rating === n ? "bg-neutral-900 text-white" : "bg-[#F4F4F7] text-neutral-700"}`}>
                    {n} <span className="font-normal opacity-70">· col {["F", "H", "J"][n - 1]}</span>
                  </button>
                ))}
              </div>
            </FField>

            <FField label="Vertical — Setup!E14"
                    hint="⚠ FREE TEXT in their workbook, and it gates the Hotline rate. A typo silently moves a job from Hotline to Call Center.">
              <div className={`${F_CTL} justify-between`}>
                <span>{vertical}</span><ChevronDown className="size-3.5 text-neutral-400" />
              </div>
            </FField>

            <FField label="Job requiring Retail Media — Setup!E38"
                    hint="Their only Y/N list on this sheet. N zeroes Tech Specific Recovery entirely.">
              <button onClick={() => setRetailMedia(!retailMedia)} className="flex h-9 items-center gap-2.5 rounded-xl bg-[#F4F4F7] px-3">
                <FToggle on={retailMedia} />
                <span className="text-[13px] font-medium">{retailMedia ? "Y — Tech Recovery applies" : "N — Tech Recovery zeroed"}</span>
              </button>
            </FField>
          </FGrid>

          <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-[#F4F4F7] px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setPvOverride(!pvOverride)} className="flex items-center gap-2.5">
                <FToggle on={pvOverride} />
                <span className="text-[13px] font-semibold">Executive perceived-value override</span>
              </button>
              <span className="rounded-md bg-white px-2 py-[2px] text-[12px] font-medium tabular-nums">−10%</span>
            </div>
            <p className="text-[11px] leading-snug text-neutral-600">
              <span className="font-medium text-neutral-800">Assumptions!G32, and it is a single switch on every cost line at once.</span>{" "}
              Their §3.2 asks for perceived value <i>per component</i>. It has sat on <span className="font-mono">NO</span> in all three books.
            </p>
          </div>
        </FCard>

        {/* THE ENGINE */}
        <FCard
          desc="Every row: what it is, the quantity and where the quantity comes from, the cost it reads, the rate that fired and which Assumptions row it came from, and any gate that could zero it."
          action={<FIllus>values illustrative · rules measured</FIllus>}
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-[#FAFAFB]">
            <table className="w-full caption-bottom text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-14">Cell</th><th>Line</th><th className="w-32">Qty</th>
                  <th className="w-52">Cost source</th><th className="w-20">Rate</th>
                  <th className="w-44">Gate</th><th className="w-24 !text-right">Per unit</th>
                </tr>
              </thead>
              <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                <TierHead n="Tier 1">Raw — direct</TierHead>
                <BURows rows={TIER_RAW} />
                <TierHead n="Tier 1">Raw — inbound freight</TierHead>
                <BURows rows={TIER_FREIGHT} />
                <tr><td colSpan={6} className="!pt-3 text-right text-[11px] font-bold uppercase tracking-wider text-neutral-600">Sub-total — raw costs</td>
                    <td className="!pt-3 text-right text-[14px] font-bold tabular-nums">$103.21</td></tr>
                <TierHead n="Tier 2">Hard recovery</TierHead>
                <BURows rows={TIER_HARD} />
                <TierHead n="Tier 3">Other recovery</TierHead>
                <BURows rows={TIER_OTHER.map((r) =>
                  r.cell === "72" ? { ...r, amount: retailMedia ? "1.66" : "—" } :
                  r.cell === "69" ? { ...r, amount: vertical === "Beauty" ? "0.99" : "—" } :
                  r.cell === "70" ? { ...r, amount: vertical === "Beauty" ? "—" : "0.66" } : r)} />
                <tr className="border-t-2 border-neutral-900">
                  <td colSpan={6} className="!pt-3 text-right text-[11px] font-bold uppercase tracking-wider text-neutral-700">Total unit cost</td>
                  <td className="!pt-3 text-right text-[16px] font-bold tabular-nums">${active.cost}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <FNote>
              <span className="font-semibold text-neutral-800">A ringed quantity is editable; a linked one is not.</span> Their workbook
              protects this sheet and unlocks exactly eight cells — <span className="font-mono">E11 E12 E37–E42</span> — which are precisely
              the rows with no sheet to pull a quantity from. Everything else reads <span className="font-mono">Other Costs</span>.
            </FNote>
            <FWarn>
              <span className="font-semibold">PF&D ×1.2 is not a rate anywhere.</span> It is the literal
              <span className="font-mono"> (6/5)</span> inside <span className="font-semibold">51 formulas per book — 153 cells</span> across the
              three. It belongs in the policy layer with an owner and a date. <FGap>PF&D has no home</FGap>
            </FWarn>
          </div>

          <div className="mt-3">
            <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-neutral-600">
              Six unit populations — pick what this screen shows
            </p>
            <div className="flex gap-2">
              {POPS.map((p) => (
                <button key={p.col} onClick={() => setPop(p.col)}
                  className={`flex-1 rounded-xl px-3 py-2 text-left ${p.col === pop ? "bg-neutral-900 text-white" : "bg-[#F4F4F7]"}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{p.col} · {p.units} units</div>
                  <div className="mt-0.5 text-[13px] font-semibold tabular-nums">${p.cost}</div>
                  <div className="truncate text-[10px] opacity-70">{p.name}</div>
                </button>
              ))}
            </div>
          </div>
        </FCard>

        {/* ── 6 · BREAKOUT ────────────────────────────────────────────── */}
        <FBand n="6">Breakout — the client price list</FBand>

        <FCard
          title="One row per part, priced at nine margins at once"
          desc="Every column is derived from the bill of material. In their workbook the only human input on a row is the line number; the rest is VLOOKUP."
          action={<FIllus>values illustrative · rules measured</FIllus>}
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-[#FAFAFB]">
            <table className="w-full caption-bottom text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-10">#</th><th className="w-28">Vendor</th><th className="w-32">Part number</th>
                  <th>Description</th><th className="w-14 !text-right">Qty</th>
                  <th className="w-24 !text-right">Unit cost</th><th className="w-28 !text-right">SG&amp;A alloc.</th>
                  <th className="w-28 !text-right">Total ACM</th><th className="w-24">Source</th>
                </tr>
              </thead>
              <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                {[["1","—","264714-S1","OCT26 UD — 2B CAN-S1",1,"38.40","11.52","49.92"],
                  ["2","Duraco","ADH-00042-003","TAPE BRON CLR .03 × .5 × 2.5",1,"0.26","0.08","0.34"],
                  ["3","Kendex","RDS-MTD-28","MERCHANDISER TRAY DIVIDER",8,"0.51","0.12","4.20"],
                  ["4","QuadExpress","MO-FTG056","FOCUS TESTER GRAPHIC",1,"6.65","2.00","8.65"]].map((r) => (
                  <tr key={String(r[0])}>
                    <td className="text-neutral-500">{r[0]}</td>
                    <td className="text-neutral-700">{r[1]}</td>
                    <td className="font-mono text-[12px] text-neutral-700">{r[2]}</td>
                    <td className="text-neutral-800">{r[3]}</td>
                    <td className="text-right tabular-nums text-neutral-600">{r[4]}</td>
                    <td className="text-right tabular-nums">${r[5]}</td>
                    <td className="text-right tabular-nums text-neutral-600">${r[6]}</td>
                    <td className="text-right font-semibold tabular-nums">${r[7]}</td>
                    <td><span className="rounded-md bg-white px-2 py-[2px] text-[11px] text-neutral-600">from BOM</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <FWarn>
            <span className="font-semibold">Their Breakout is unprotected, and on two jobs of three the derivation has been overwritten by hand</span> —
            79 typed part costs on LG, 70 on NOYZ, against 10 on Morphe where the column is still a VLOOKUP.
            So here the cost is read from the BOM line, and a disagreement becomes a <span className="font-semibold">marked override with a reason</span>,
            never a pasted value that breaks the link and leaves no trace.
          </FWarn>
        </FCard>

        {/* THE LADDER — the decision surface */}
        <FCard
          title="Choose the margin"
          desc="Their sheet computes all nine at once and heat-maps exactly two rows — the gap to the client's budget and the gap to the market comp. That colour IS the decision aid; it is the only conditional formatting in nineteen sheets."
          action={
            <button onClick={() => setBudgetSet(!budgetSet)} className="flex items-center gap-2.5 rounded-xl bg-[#F4F4F7] px-3 py-2">
              <FToggle on={budgetSet} />
              <span className="text-[12.5px] font-medium">{budgetSet ? "Client budget $520 entered" : "Client budget empty — as on Morphe"}</span>
            </button>
          }
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-white p-2">
            <table className="w-full text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-44">Contribution margin</th>
                  {MARGINS.map((m, i) => (
                    <th key={m} className="!text-center">
                      <button onClick={() => setChosen(i)}
                        className={`rounded-md px-2 py-0.5 tabular-nums ${i === chosen ? "bg-neutral-900 text-white" : ""}`}>
                        {(m * 100).toFixed(0)}%
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Ladder label="Client price / unit" values={price} chosen={chosen} fmt={(v) => `$${v.toFixed(0)}`} />
                <Ladder label="Profit / unit" values={profit} chosen={chosen} fmt={(v) => `$${v.toFixed(0)}`} />
                <Ladder label="∆ Client Budget" inert={!budgetSet} chosen={chosen}
                        values={price.map((p) => 520 - p)} fmt={(v) => `${v >= 0 ? "+" : ""}$${v.toFixed(0)}`} />
                <Ladder label="∆ Market Comp" inert={!budgetSet} chosen={chosen}
                        values={price.map((p) => 545 - p)} fmt={(v) => `${v >= 0 ? "+" : ""}$${v.toFixed(0)}`} />
                <Ladder label="Extended job value" values={price.map((p) => p * 127)} chosen={chosen}
                        fmt={(v) => `$${(v / 1000).toFixed(0)}k`} />
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-stretch gap-3">
            <div className="flex-1 rounded-2xl bg-neutral-900 px-5 py-4 text-white">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400">The decision — and their sheet does not record it</p>
              <p className="mt-1 text-[22px] font-bold leading-none tabular-nums">
                {(MARGINS[chosen] * 100).toFixed(0)}% · ${price[chosen].toFixed(2)} / unit
              </p>
              <p className="mt-1.5 text-[11px] leading-snug text-neutral-400">
                Nine options are stored; <span className="font-semibold text-white">which one was quoted is written nowhere.</span> It is the
                number their §3.7.1 conversion model needs most, and the one our estimate must keep.
              </p>
            </div>
            <div className="w-[400px] rounded-2xl bg-[#F4F4F7] px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500">Check to bottom up</p>
              <p className="mt-1 text-[15px] font-semibold tabular-nums text-emerald-700">0.00 ✓</p>
              <p className="mt-1 text-[11px] leading-snug text-neutral-600">
                Their own reconciliation, kept. There are <span className="font-semibold">five</span> of these written by hand across the
                three books — a check somebody keeps re-adding is a feature request written in a formula.
              </p>
            </div>
          </div>

          {!budgetSet ? (
            <FWarn>
              <span className="font-semibold">This is not a rendering fault — it is Morphe.</span> `Client Budget / Unit` and
              `Market Comp / Unit` are both <span className="font-mono">0</span> on that job, so the two heat-mapped rows read
              <span className="font-mono"> #DIV/0!</span> and the only decision aid in the workbook is dead. Both fields arrive on the
              Project Request and are captured in the ballpark. <span className="font-semibold">Block 1 of Journey A feeds the last screen of Journey B.</span>
            </FWarn>
          ) : null}
        </FCard>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 · VENDOR RFQs — the one step of Journey B that is not a form
// ═══════════════════════════════════════════════════════════════════════════
//
// Erez, 2 Sept: "research the vendor RFQ, read the blueprint, create a ticket
// and screen design in the board."
//
// §3.3 is its own engine — 71 requirements — and the round trip already exists
// in their practice: `264511 BALLPARK TEMPLATE` is an OUTBOUND document, a line
// list with UNIT COST empty on every row of its first sheet, sent to a vendor
// to fill in. So this screen is not a new behaviour; it is the behaviour they
// already have, with a record around it.
//
// ⛔ THE SCHEMA IS ALREADY THERE AND COMPLETELY EMPTY. sql/020 defines `rfqs`
// (with `white_label` and a `template` jsonb), `vendor_quotes` (`source_format`
// pdf|excel|email, `raw_path`, `parsed`, `parse_note`), `vendor_quote_lines`
// (with `valid_until` — §3.2's cost-validity statement) and `vendor_scorecard`
// (six dimensions). Measured on the live box: rfqs 0 · vendor_quotes 0 ·
// vendor_quote_lines 0 · vendor_scorecard 0. Nothing has ever been written.

const RFQ_LINES = [
  { n: "1", part: "TR-4412", desc: "Powder-coat steel frame, 4-shelf", spec: "1.2mm CRS, RAL 9005 matt", qty: 127, last: "38.40", target: "34.56", got: "36.10", state: "in" },
  { n: "2", part: "AC-0918", desc: "Acrylic tester tray, 6-well", spec: "6mm cast, polished edge", qty: 762, last: "4.15", target: "3.74", got: "3.68", state: "in" },
  { n: "3", part: "LT-2200", desc: "LED light bar, 12V 400mm", spec: "4000K, 90 CRI, IP20", qty: 254, last: "9.75", target: "8.78", got: "—", state: "await" },
  { n: "4", part: "HW-0071", desc: "Magnet set, 8mm neodymium", spec: "N42, nickel plated", qty: 1016, last: "0.26", target: "0.23", got: "0.31", state: "over" },
  { n: "5", part: "DS-LS002CL", desc: "Laser-cut side, clear", spec: "3mm PETG, flame polished", qty: 254, last: "11.40", target: "10.26", got: "—", state: "await" },
];

function RfqState({ s }: { s: string }) {
  const map: Record<string, [string, string]> = {
    in:    ["in — under target", "bg-emerald-100 text-emerald-900"],
    over:  ["over target", "bg-amber-100 text-amber-900"],
    await: ["awaiting", "bg-[#F4F4F7] text-neutral-600"],
  };
  const [label, skin] = map[s] ?? ["—", ""];
  return <span className={`rounded-md px-2 py-[2px] text-[11.5px] font-medium ${skin}`}>{label}</span>;
}

const SCORE = [
  ["Quote accuracy", "how close the quote was to the invoice"],
  ["Delivery reliability", "did it arrive when they said"],
  ["Quality score", "field issues on jobs they supplied"],
  ["Responsiveness", "how long they take to answer an RFQ"],
  ["Price competitiveness", "against the other quotes on the same line"],
  ["Lead-time reliability", "quoted lead time against actual"],
];

export function VendorRfqScreen() {
  const [stage, setStage] = useState<"compose" | "sent" | "parsed">("parsed");
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [reduction, setReduction] = useState(10);

  return (
    <div className="nopan nodrag w-[1620px] text-neutral-900" onWheelCapture={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-4">
        <FBand n="2">Vendor RFQs</FBand>

        {/* THE LIFECYCLE — an RFQ is an EVENT, not a field */}
        <FCard
          title="An RFQ is an event with a lifecycle, not a field on a form"
          desc="Composed → sent → awaiting → parsed → accepted or rejected. Each step carries a time and an actor, because a vendor price that arrives with no record of what was asked cannot be defended later."
          action={
            <div className="flex gap-1.5">
              {(["compose", "sent", "parsed"] as const).map((s) => (
                <button key={s} onClick={() => setStage(s)}
                  className={`h-8 rounded-xl px-3 text-[12px] font-medium capitalize ${
                    stage === s ? "bg-neutral-900 text-white" : "bg-[#F4F4F7] text-neutral-700"}`}>{s}</button>
              ))}
            </div>
          }
        >
          <FGrid>
            <FField label="What is being asked for"
                    hint="§3.3's costing template — the SAME structure to every vendor, which is their own stated purpose: apples-to-apples comparison and seamless ingestion.">
              <div className={`${F_CTL} justify-between`}><span>Costing template · 5 lines from the BOM</span><ChevronDown className="size-3.5 text-neutral-400" /></div>
            </FField>
            {/* ⛔ MEASURED, AND IT CORRECTS A FIGURE I HAD BEEN REPEATING.
                27 distinct vendor strings sit on bom_line. TWO match the D365
                master by normalised name — and they are COLOR DIRECT and
                Color Direct, the same vendor written twice. So exactly ONE
                entity of 21 resolves, against a master of 2,060. Kendex, the
                vendor in this very mock, is not one of them. */}
            <FField label="Vendor"
                    hint="⚠ Kendex does not resolve to their D365 master. ONE vendor entity of 21 on the BOM does — against a master of 2,060. An RFQ needs an address.">
              <div className={`${F_CTL} justify-between`}>
                <span>Kendex <span className="text-amber-700">· unresolved</span></span>
                <ChevronDown className="size-3.5 text-neutral-400" />
              </div>
            </FField>
            <FField label="Target guidance per component — R097"
                    hint="Their words: 'target price guidance for each component, potentially with a defined reduction expectation, e.g. target minus X%', to anchor the negotiation.">
              <div className="flex items-center gap-2">
                <div className={`${F_CTL} w-24 justify-center font-semibold tabular-nums`}>−{reduction}%</div>
                <input type="range" min={0} max={25} value={reduction}
                       onChange={(e) => setReduction(Number(e.target.value))} className="flex-1" />
              </div>
            </FField>
          </FGrid>

          <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-[#F4F4F7] px-4 py-3">
            <button onClick={() => setWhiteLabel(!whiteLabel)} className="flex items-center gap-2.5">
              <FToggle on={whiteLabel} />
              <span className="text-[13px] font-semibold">White-label — send anonymised</span>
            </button>
            <p className="flex-1 text-[11px] leading-snug text-neutral-600">
              <span className="font-medium text-neutral-800">Their own §10.2 agent, and the column already exists — `rfqs.white_label`.</span>{" "}
              Asks for current market pricing without revealing that Outform is asking, so a quote is not shaped by who is buying.
              {whiteLabel ? <span className="font-semibold text-neutral-900"> The vendor sees a spec and a quantity, never a customer.</span> : null}
            </p>
          </div>
        </FCard>

        {/* THE LINES */}
        <FCard
          desc="The same lines the estimate will price, with what we paid last time, what we are asking for, and what came back. A line still out is AWAITING — never missing, and never quietly priced at the old number."
          action={<FIllus>values illustrative · rules measured</FIllus>}
        >
          <div className="relative w-full overflow-x-auto rounded-2xl bg-[#FAFAFB]">
            <table className="w-full caption-bottom text-[13px]">
              <thead className={F_THEAD}>
                <tr>
                  <th className="w-10">#</th><th className="w-32">Part</th><th>Description</th>
                  <th className="w-56">Spec sent</th><th className="w-16 !text-right">Qty</th>
                  <th className="w-24 !text-right">Last paid</th><th className="w-24 !text-right">Target</th>
                  <th className="w-24 !text-right">Quoted</th><th className="w-36">State</th>
                </tr>
              </thead>
              <tbody className="[&_td]:whitespace-nowrap [&_td]:p-2 [&_td]:align-middle">
                {RFQ_LINES.map((r) => (
                  <tr key={r.n} className={r.state === "await" ? "opacity-60" : ""}>
                    <td className="text-neutral-500">{r.n}</td>
                    <td className="font-mono text-[12px] text-neutral-700">{r.part}</td>
                    <td className="text-neutral-900">{r.desc}</td>
                    <td className="text-[12px] text-neutral-600">{r.spec}</td>
                    <td className="text-right tabular-nums text-neutral-600">{r.qty}</td>
                    <td className="text-right tabular-nums text-neutral-600">${r.last}</td>
                    <td className="text-right tabular-nums font-medium">${r.target}</td>
                    <td className="text-right tabular-nums font-semibold">
                      {r.got === "—" ? <span className="text-neutral-300">—</span> : `$${r.got}`}
                    </td>
                    <td><RfqState s={r.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <FNote>
              <span className="font-semibold text-neutral-800">Every accepted price is written with its quote date.</span> §3.2's cost-validity
              statement, and their acceptance criterion is <span className="font-semibold">95% of inputs under 30 days old</span>.
              The column exists — <span className="font-mono">vendor_quote_lines.valid_until</span>.
            </FNote>
            <FNote>
              <span className="font-semibold text-neutral-800">Scott: "they're custom every time — we're taking that fresh quote every time."</span>{" "}
              So a stored price is only ever a reference point, and the staleness clock is what stops an old number reading as current.
            </FNote>
            <FWarn>
              Line 4 came back <span className="font-semibold">over target</span> at $0.31 against $0.23 — the conversation this screen
              exists to start, and exactly the prompt Data Pilot scripted.{" "}
              <span className="font-semibold">But note who we are talking to.</span> Of 27 vendor strings on their bills of material,
              <span className="font-semibold"> 21 real entities</span> and <span className="font-semibold">ONE</span> resolves to the
              2,060-name D365 master. Six pairs differ by case alone — <span className="font-mono">DURACO/Duraco</span>,{" "}
              <span className="font-mono">KENDEX/Kendex</span>, <span className="font-mono">Bay City/Bay CIty</span> — and the list
              also holds <span className="font-mono">0.0</span>, <span className="font-mono">CUSTOMER SUPPLIED</span> and{" "}
              <span className="font-mono">FIXTURE VENDOR</span>. <FGap>vendor identity is a prerequisite, not a detail</FGap>
            </FWarn>
          </div>
        </FCard>

        {/* WHAT COMES BACK */}
        <FCard
          title="Whatever comes back, in whatever shape"
          desc="§3.3 R166 — PDF, Excel, or the body of an email. This is the one genuinely agentic step in the whole engine, and their document gives it one line."
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#F4F4F7] p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500">What arrived</p>
              <p className="mt-1.5 text-[13px] font-medium">Kendex_Quote_Sept.pdf</p>
              <p className="mt-0.5 text-[11.5px] text-neutral-600">received 14:22 · 3 of 5 lines parsed · 2 unmatched</p>
              <p className="mt-3 rounded-xl bg-white px-3 py-2 text-[11.5px] leading-snug text-neutral-600">
                The raw file is kept. <span className="font-medium text-neutral-800">A price with no document behind it cannot be defended</span>,
                and <span className="font-mono">vendor_quotes.raw_path</span> is where it goes.
              </p>
            </div>
            <div className="rounded-2xl bg-[#F4F4F7] p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500">What could not be matched</p>
              <p className="mt-1.5 text-[13px] font-medium">2 lines need a human</p>
              <p className="mt-0.5 text-[11.5px] text-neutral-600">
                "MAG SET 8MM NEO" against our HW-0071 · a price quoted per 100, not per unit
              </p>
              <p className="mt-3 rounded-xl bg-white px-3 py-2 text-[11.5px] leading-snug text-neutral-600">
                <span className="font-medium text-neutral-800">Unmatched is a queue, not an error.</span>{" "}
                <span className="font-mono">parse_note</span> holds why, and nothing is written to a price until somebody agrees it.
              </p>
            </div>
          </div>
        </FCard>

        {/* THE SCORECARD */}
        <FCard
          title="Vendor scorecard"
          desc="§3.3 R168 scores price, lead time, quality and responsiveness. §10.1 updates it per job close. The table exists with six columns."
          action={<span className="rounded-md bg-rose-50 px-2 py-[3px] text-[11.5px] font-semibold text-rose-800">0 rows · nothing has ever been scored</span>}
        >
          <div className="grid grid-cols-3 gap-3">
            {SCORE.map(([k, why]) => (
              <div key={k} className="rounded-2xl bg-[#F4F4F7] px-4 py-3">
                <p className="text-[12.5px] font-semibold text-neutral-800">{k}</p>
                <p className="mt-1 text-[19px] font-semibold tabular-nums text-neutral-300">—</p>
                <p className="mt-0.5 text-[11px] leading-snug text-neutral-600">{why}</p>
              </div>
            ))}
          </div>
          <FWarn>
            <span className="font-semibold">This is a collection problem, not an architecture problem — and it is the same shape as the win/loss gap.</span>{" "}
            §3.2 asks for "vendor mix optimisation based on cost, lead time and performance" and today only <span className="font-semibold">cost</span> exists.
            A cheap quote from an unreliable vendor is indistinguishable from a cheap quote from a reliable one.
            ⭐ <span className="font-semibold">The repair starts the day the first job closes inside the system</span>, which is the argument for
            wiring the scorecard write EARLY rather than last. <FGap>0 rfqs · 0 quotes · 0 scorecards, measured on the live box</FGap>
          </FWarn>
        </FCard>

        {/* ⭐ THE THIRD PARTY — added after finding R357. Everything above this
            models a two-party loop, estimator and vendor. Their architecture is
            three-party: R357 "Connection to Project Management agent creates a
            feedback loop to RFQs and supplier feedback with auto prompts on
            success of quotes received", and R437 syncs RFQs to the PM tool
            BIDIRECTIONALLY. An RFQ is a supply-chain TASK as well as a document. */}
        <FCard
          title="An RFQ is also a task, and it lives in two systems"
          desc="Their R357 puts a Project Management agent in this loop, and R437 syncs RFQs to the PM tool both ways. So the round trip above is only two of its three parties."
        >
          <div className="grid grid-cols-3 gap-3">
            {[
              ["THE ESTIMATE", "owns the RFQ", [
                "composes the costing template from the BOM",
                "holds the target guidance and the Incoterm",
                "writes the price with its quote date",
                "the single writer — one record, one owner",
              ], true],
              ["THE PM TOOL", "holds a task that references it", [
                "owner, due date, chase state",
                "R437 · Smartsheet, bidirectional",
                "R357 · auto prompts on quotes received",
                "reads the RFQ; never writes the price",
              ], false],
              ["THE VENDOR", "answers, in whatever shape", [
                "PDF · Excel · prose in an email body",
                "R423 also names VENDOR PORTALS",
                "a reply may come from any recipient",
                "quotes get revised — Q32125(R5)",
              ], false],
            ].map(([name, role, lines, owner]) => (
              <div key={name as string}
                   className={`rounded-2xl p-4 ${owner ? "bg-neutral-900 text-white" : "bg-[#F4F4F7]"}`}>
                <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${owner ? "text-neutral-400" : "text-neutral-500"}`}>
                  {name as string}
                </p>
                <p className="mt-1 text-[14px] font-semibold">{role as string}</p>
                <ul className={`mt-2.5 flex flex-col gap-1.5 text-[11.5px] leading-snug ${owner ? "text-neutral-300" : "text-neutral-600"}`}>
                  {(lines as string[]).map((l) => <li key={l}>· {l}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <FWarn>
            <span className="font-semibold">The clause does not say which system OWNS an RFQ, and two systems that both think they
            own a record is the oldest integration failure there is.</span>{" "}
            The recommendation above is drawn deliberately: <span className="font-semibold">the estimate owns it, the PM tool
            references it — one writer, one reader.</span> A due date and a chase belong to project management; a price and an
            Incoterm belong to the estimate. <FGap>the PM sync is out of release one — but the design must not preclude it</FGap>
          </FWarn>
        </FCard>

        <FNote>
          <span className="font-semibold text-neutral-800">Two cadences, and only one of them is this screen.</span> §3.3's own rhythm is a
          MONTHLY refresh across sheet goods, metals, plastics, hardware, finishes and labour rates (R187–R194) — that is a scheduled pipeline
          and it belongs to the engine. <span className="font-semibold text-neutral-800">The PER-JOB round trip is what an estimator does, and it
          is what they already do by hand.</span> Building the per-job one first means the monthly one inherits a parser that has been proved
          against real replies.
        </FNote>
      </div>
    </div>
  );
}
